import React, { useEffect, useState, useContext } from 'react';
import { Grid, Typography, useMediaQuery } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import GridGallery from 'react-grid-gallery';
import ImagesContext from '../contexts/ImagesContext';
import { colors } from '../theme';

const useStyles = makeStyles((theme) => ({
  gallery: {
    // The library floats its tiles; this keeps the section from collapsing.
    overflow: 'hidden',
    '& img': { transition: 'transform 0.35s ease' },
    '& .tile:hover img, & div:hover > img': { transform: 'scale(1.04)' },
  },
  placeholder: {
    height: 200,
    borderRadius: 10,
    backgroundColor: 'rgba(14,35,64,0.07)',
    animation: '$pulse 1.4s ease-in-out infinite',
  },
  '@keyframes pulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.5 },
  },
  empty: {
    textAlign: 'center',
    padding: theme.spacing(6, 2),
    color: theme.palette.text.secondary,
    border: `2px dashed rgba(14,35,64,0.12)`,
    borderRadius: 16,
  },
  emptyIcon: {
    fontSize: 48,
    color: colors.green,
    marginBottom: theme.spacing(1),
  },
}));

// The grid library needs each photo's real size to lay out rows,
// so load each image once to read its dimensions.
function measure(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () =>
      resolve({
        src: url,
        thumbnail: url,
        thumbnailWidth: img.naturalWidth,
        thumbnailHeight: img.naturalHeight,
      });
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

function tileStyle() {
  return {
    width: this.props.item.vwidth,
    height: this.props.height,
    overflow: 'hidden',
    borderRadius: 10,
  };
}

// Phone tiles are square, so crop each photo to fill its tile.
function squareThumbnailStyle() {
  return { width: '100%', height: '100%', objectFit: 'cover' };
}

const MARGIN = 6;

// Tracks an element's width so the phone grid can size its tiles.
function useElementWidth() {
  const [node, setNode] = useState(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!node) return undefined;
    const update = () => setWidth(Math.floor(node.clientWidth));
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return [setNode, width];
}

export default function Gallery() {
  const classes = useStyles();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('xs'));
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'));
  const [galleryRef, galleryWidth] = useElementWidth();
  const { imageURLs, imagesLoaded } = useContext(ImagesContext);
  const [images, setImages] = useState([]);
  const [measured, setMeasured] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!imagesLoaded) return undefined;
    Promise.all(imageURLs.map(measure)).then((list) => {
      if (!cancelled) {
        setImages(list.filter(Boolean));
        setMeasured(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [imageURLs, imagesLoaded]);

  if (!measured) {
    return (
      <Grid container spacing={2} aria-label="Loading photos">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Grid item xs={6} md={4} key={i}>
            <div className={classes.placeholder} />
          </Grid>
        ))}
      </Grid>
    );
  }

  if (images.length === 0) {
    return (
      <div className={classes.empty}>
        <PhotoLibraryIcon className={classes.emptyIcon} />
        <Typography>Photos of recent projects are coming soon.</Typography>
      </div>
    );
  }

  // On phones and tablets, show an even grid (2 or 3 columns). The library
  // sizes rows from each photo's shape, so tall photos got squeezed into
  // thin strips. Telling it every photo is square, with tiles just over
  // 1/2 or 1/3 of the width, gives exactly that many per row. The
  // full-size viewer still shows the whole photo.
  const columns = isPhone ? 2 : 3;
  const phoneGrid = isTablet && galleryWidth > 0;
  const tileSize = Math.ceil((galleryWidth - columns * 2 * MARGIN) / columns);
  const shownImages = phoneGrid
    ? images.map((image) => ({ ...image, thumbnailWidth: 1, thumbnailHeight: 1 }))
    : images;

  return (
    <div className={classes.gallery} ref={galleryRef}>
      <GridGallery
        images={shownImages}
        enableImageSelection={false}
        rowHeight={phoneGrid ? tileSize : 240}
        margin={MARGIN}
        backdropClosesModal
        showLightboxThumbnails
        tileViewportStyle={tileStyle}
        thumbnailStyle={phoneGrid ? squareThumbnailStyle : undefined}
      />
    </div>
  );
}
